import { Box, Checkbox, Divider, Link, Stack, Typography } from '@mui/joy';
import { CaretRight } from '@phosphor-icons/react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Confirmation } from 'core/components';
import { AgGridFilterOption } from 'core/enums';
import { AlternateVersionHelper } from 'core/helpers';
import { useAgGridClientTableContext } from 'core/states';
import {
  CustomerQuoteDetails,
  CustomerQuoteDetailsTab,
} from 'quotes/components/CustomerQuoteDetails';
import QuotePartUpdateMarginForm from 'quotes/components/QuotePartUpdateMarginForm';
import { QuotePartUpsertLine } from 'quotes/components/QuotePartUpsertLine';
import { QuoteEntityType } from 'quotes/enums';
import { useSelectedQuotePartContext } from 'quotes/states';
import { ICustomerQuote, IQuotePart } from 'quotes/types';
import { Color, Level, Size } from 'theme/enums';

import { ORDER_WARNING_TAB_MAP } from './constants';
import { AlternateOrderPartValidationWarning } from './enums';
import { ISubmitValidationResult, IValidationWarningItem } from './types';
import { CELL_LINE_EDIT_WARNING_PREFIX } from './utils';

const EDIT_LINE_WARNINGS = new Set([
  AlternateOrderPartValidationWarning.DISALLOWED_MANUFACTURER,
  AlternateOrderPartValidationWarning.EXTERNAL_PART_STATUS_ABANDONED,
  AlternateOrderPartValidationWarning.INVALID_QUANTITY_DECIMAL,
  AlternateOrderPartValidationWarning.MISSING_DESCRIPTION,
  AlternateOrderPartValidationWarning.MISSING_MANUFACTURER,
  AlternateOrderPartValidationWarning.MISSING_TAXONOMY,
  AlternateOrderPartValidationWarning.MISSING_VENDOR,
  AlternateOrderPartValidationWarning.NOTE_BUYER_TOO_LONG,
  AlternateOrderPartValidationWarning.NOTE_PICKER_TOO_LONG,
  AlternateOrderPartValidationWarning.NOTE_SUPSHIP_TOO_LONG,
  AlternateOrderPartValidationWarning.PART_NUMBER_TOO_LONG,
]);

const MAX_VISIBLE_LINES = 25;

interface ILineNumbersProps {
  itemId: string;
  lines: number[];
  onLineClick: (line: number) => void;
}

const LineNumbers = ({ itemId, lines, onLineClick }: ILineNumbersProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const { t } = useTranslation();

  if (!lines?.length) {
    return null;
  }

  const isTruncated = lines.length > MAX_VISIBLE_LINES;
  const visibleLines = isExpanded || !isTruncated ? lines : lines.slice(0, MAX_VISIBLE_LINES);

  return (
    <Box component="span" sx={{ marginLeft: 0.5 }}>
      {visibleLines.map((line, index) => (
        <Link
          key={`${itemId}-line-${line}`}
          component="button"
          level={Level.TITLE_MD}
          color={Color.PRIMARY}
          sx={{
            cursor: 'pointer',
            '&:hover': { textDecoration: 'underline' },
          }}
          onClick={() => onLineClick(line)}
        >
          {index > 0 && ', '}
          {line}
        </Link>
      ))}
      {(!isTruncated || isExpanded) && '.'}
      {isTruncated && !isExpanded && '...'}
      {isTruncated && (
        <Link
          component="button"
          level={Level.BODY_SM}
          color={Color.PRIMARY}
          onClick={() => setIsExpanded(!isExpanded)}
          sx={{ display: 'block', marginTop: 0.5 }}
        >
          {isExpanded ? t('labelShowLess') : t('labelShowAllLines', { count: lines.length })}
        </Link>
      )}
    </Box>
  );
};

interface IProps {
  warnings: ISubmitValidationResult;
  quote: ICustomerQuote;
  onUpdateQuote: (quote: ICustomerQuote) => void;
  quoteEntityType: QuoteEntityType;
  onCancel: () => void;
}

export const SubmitValidationWarning = ({
  warnings,
  quote,
  onUpdateQuote,
  quoteEntityType,
  onCancel,
}: IProps) => {
  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [detailsDefaultTab, setDetailsDefaultTab] = useState<CustomerQuoteDetailsTab>();
  const [isEditLineOpen, setIsEditLineOpen] = useState(false);
  const [isUpdateMarginOpen, setIsUpdateMarginOpen] = useState(false);

  const { t } = useTranslation();
  const { gridApi } = useAgGridClientTableContext();
  const { selectQuotePart, clearSelectedQuotePart } = useSelectedQuotePartContext();

  const isAlternateVersionEnabled = AlternateVersionHelper.isAlternateVersionEnabled({ quote });

  const allLineIds = useMemo(
    () => warnings.lineWarnings.map((item) => item.id),
    [warnings.lineWarnings]
  );

  const isAllChecked = checkedIds.size === allLineIds.length && allLineIds.length > 0;
  const isIndeterminate = checkedIds.size > 0 && checkedIds.size < allLineIds.length;

  const onToggleItem = (id: string) => {
    setCheckedIds((prev) => {
      const next = new Set(prev);

      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }

      return next;
    });
  };

  const onToggleAll = () => {
    if (isAllChecked) {
      setCheckedIds(new Set());

      return;
    }

    setCheckedIds(new Set(allLineIds));
  };

  const onShowSelectedLines = () => {
    const selectedLineNumbers = warnings.lineWarnings
      .filter((item) => checkedIds.has(item.id) && item.lines?.length)
      .flatMap((item) => item.lines)
      .filter((line, index, self) => self.indexOf(line) === index);

    if (gridApi && selectedLineNumbers.length > 0) {
      const conditions = selectedLineNumbers.map((line) => ({
        filterType: 'number',
        type: AgGridFilterOption.EQUALS,
        filter: line,
      }));

      const lineFilter =
        conditions.length === 1
          ? conditions[0]
          : {
              filterType: 'number',
              operator: 'OR',
              conditions,
            };

      gridApi.setFilterModel({ line: lineFilter });
    }

    onCancel();
  };

  const onLineClick = (warningId: string, lineNumber: number) => {
    if (!gridApi) {
      return;
    }

    let quotePart: IQuotePart | null = null;

    gridApi.forEachNode((node) => {
      if (node.data?.line === lineNumber) {
        quotePart = node.data as IQuotePart;
        gridApi.ensureIndexVisible(node.rowIndex, 'middle');

        setTimeout(() => {
          gridApi.flashCells({ rowNodes: [node], flashDuration: 2000, fadeDuration: 1000 });
        }, 500);
      }
    });

    if (!quotePart) {
      onCancel();

      return;
    }

    selectQuotePart(quotePart);

    const isCellLineEditWarning = warningId.startsWith(CELL_LINE_EDIT_WARNING_PREFIX);
    const isEnumLineEditWarning = EDIT_LINE_WARNINGS.has(
      warningId as AlternateOrderPartValidationWarning
    );

    if (isCellLineEditWarning || isEnumLineEditWarning) {
      setIsUpdateMarginOpen(false);
      setIsEditLineOpen(true);
    } else {
      setIsEditLineOpen(false);
      setIsUpdateMarginOpen(true);
    }
  };

  const onOpenOrderDetails = (warningId: string) => {
    setIsEditLineOpen(false);
    setIsUpdateMarginOpen(false);
    clearSelectedQuotePart();
    setDetailsDefaultTab(ORDER_WARNING_TAB_MAP[warningId]);
    setIsDetailsOpen(true);
  };

  const hasSelectedLines = warnings.lineWarnings.some(
    (item) => checkedIds.has(item.id) && item.lines?.length
  );

  const message = (
    <Stack sx={{ gap: 0 }}>
      {warnings.orderWarnings.length > 0 && (
        <>
          <Typography level={Level.TITLE_SM} sx={{ color: 'text.secondary', paddingTop: 1 }}>
            {t('titleOrderWarnings')} ({warnings.orderWarnings.length})
          </Typography>

          {warnings.orderWarnings.map((item: IValidationWarningItem) => (
            <Box key={item.id} sx={{ padding: '8px 0' }}>
              <Typography level={Level.BODY_MD} sx={{ fontWeight: 500 }}>
                {item.warning}
              </Typography>
              {item.action && (
                <Link
                  level={Level.BODY_SM}
                  color={Color.PRIMARY}
                  startDecorator={<CaretRight style={{ fontSize: 14 }} />}
                  onClick={() => onOpenOrderDetails(item.id)}
                  sx={{ paddingLeft: 4 }}
                >
                  {item.action}
                </Link>
              )}
              {item.description && (
                <Typography level={Level.BODY_SM} sx={{ color: 'text.tertiary', paddingLeft: 4 }}>
                  {item.description}
                </Typography>
              )}
            </Box>
          ))}
        </>
      )}

      {warnings.lineWarnings.length > 0 && (
        <>
          {warnings.orderWarnings.length > 0 && <Divider sx={{ marginY: 1 }} />}

          <Typography level={Level.TITLE_SM} sx={{ color: 'text.secondary', paddingTop: 1 }}>
            {t('titleLineWarnings')} ({warnings.lineWarnings.length})
          </Typography>

          <Stack sx={{ gap: 1, paddingTop: 1.5 }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Checkbox
                size={Size.SM}
                color={Color.PRIMARY}
                checked={isAllChecked}
                indeterminate={isIndeterminate}
                onChange={onToggleAll}
              />
              <Typography level={Level.BODY_MD} sx={{ color: 'text.tertiary', fontWeight: 500 }}>
                {t('labelSelectAll')}
              </Typography>
            </Box>

            <Divider />

            <Stack sx={{ gap: 0 }}>
              {warnings.lineWarnings.map((item: IValidationWarningItem) => {
                const isChecked = checkedIds.has(item.id);

                return (
                  <Box
                    key={item.id}
                    sx={{
                      display: 'flex',
                      gap: 1,
                      alignItems: 'center',
                    }}
                  >
                    <Checkbox
                      size={Size.SM}
                      color={Color.PRIMARY}
                      checked={isChecked}
                      onChange={() => onToggleItem(item.id)}
                    />
                    <Typography
                      level={Level.TITLE_MD}
                      sx={{ color: isChecked ? 'text.primary' : 'text.tertiary' }}
                    >
                      {item.warning}
                      {item.lines?.length > 0 && (
                        <LineNumbers
                          itemId={item.id}
                          lines={item.lines}
                          onLineClick={(line) => onLineClick(item.id, line)}
                        />
                      )}
                    </Typography>
                  </Box>
                );
              })}
            </Stack>
          </Stack>
        </>
      )}
    </Stack>
  );

  const extraButtons =
    warnings.lineWarnings.length > 0
      ? [
          <Button
            key="showSelectedLines"
            text={t('buttonLabelShowSelectedLines')}
            disabled={!hasSelectedLines}
            onClick={onShowSelectedLines}
          />,
        ]
      : [];

  return (
    <>
      <Confirmation
        isOpen={true}
        title={`${t('titleOrderPartValidationFailed')} (${warnings.count})`}
        message={message}
        onCancel={() => {
          clearSelectedQuotePart();
          onCancel();
        }}
        color={Color.DANGER}
        extraButtons={extraButtons}
        dialogSx={{ maxWidth: 600 }}
      />

      <CustomerQuoteDetails
        isOpen={isDetailsOpen}
        setIsOpen={setIsDetailsOpen}
        initialQuote={quote}
        setQuote={onUpdateQuote}
        entityType={quoteEntityType}
        defaultTab={detailsDefaultTab}
      />

      <QuotePartUpsertLine
        isOpen={isEditLineOpen}
        onClose={() => {
          setIsEditLineOpen(false);
          clearSelectedQuotePart();
        }}
        quote={quote}
        entityType={quoteEntityType}
      />

      <QuotePartUpdateMarginForm
        isOpen={isUpdateMarginOpen}
        onClose={() => {
          setIsUpdateMarginOpen(false);
          clearSelectedQuotePart();
        }}
        onSubmitSuccess={() => setIsUpdateMarginOpen(false)}
        quote={quote}
        isAlternateVersionEnabled={isAlternateVersionEnabled}
        interactive
      />
    </>
  );
};
